import React, { useCallback, useState } from "react";

export type FocusState = boolean;
export type BlurEventHandler<ElementT> = React.FocusEventHandler<ElementT>;
export type FocusEventHandler<ElementT> = React.FocusEventHandler<ElementT>;

export interface UseFocus<ElementT> {
    focus: FocusState;
    handleBlur: BlurEventHandler<ElementT>;
    handleFocus: FocusEventHandler<ElementT>;
}

/**
 * Hook to wrap handling of `onBlur` and `onFocus` and traces the `focus` state accordingly.
 *
 * @param initialFocusState of the component. Defaults to `false`.
 * @param onBlurOrigin the original `onBlur` method which should be called. Defaults to `undefined`.
 * @param onFocusOrigin the original `onFocus` method which should be called. Defaults to `undefined`.
 * @return {@link UseFocus} containing the focus state, a blur and a focus handler
 */
export const useFocus = <ElementT = Element>(
    initialFocusState: FocusState | (() => FocusState) = false,
    onBlurOrigin: BlurEventHandler<ElementT> | undefined = undefined,
    onFocusOrigin: FocusEventHandler<ElementT> | undefined = undefined,
): UseFocus<ElementT> => {
    const [focus, setFocus] = useState(initialFocusState);

    const handleBlur = useCallback(
        (event: React.FocusEvent<ElementT>) => {
            setFocus(false);
            onBlurOrigin && onBlurOrigin(event);
        },
        [onBlurOrigin, setFocus],
    );

    const handleFocus = useCallback(
        (event: React.FocusEvent<ElementT>) => {
            setFocus(true);
            onFocusOrigin && onFocusOrigin(event);
        },
        [onFocusOrigin, setFocus],
    );

    return { focus, handleBlur, handleFocus };
};
